import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/Badge';
import { Money } from '@/components/ui/Money';
import { TextLink } from '@/components/ui/TextLink';
import { formatDateOnly } from '@/lib/format';
import type { BillingRegionLabeller } from '@/lib/billing-region';
import type { ColumnSpec } from '@/lib/use-table-filters';
import type { TargetActualRow, TargetActualRowKind } from '@/lib/types';

/**
 * Shared column vocabulary for the target tile drill-down (owner 2026-10-28).
 *
 * One row per DOCUMENT behind a tile figure (`GET /targets/actuals/rows`). The
 * per-metric column lists in `./columns/*` are built from the pieces here, so a
 * record link, a company link, the project links, a money cell and a date read
 * the same in every metric's table.
 *
 * ⚠️ **TWO THINGS A ROW DOES NOT CARRY, which is why every accessor takes a
 * {@link DrilldownCellContext}:**
 * - the CONVERTED amount's currency — it is `meta.measuredInCurrency`, one per
 *   response, never per row;
 * - the billing-region NAME — the names are admin-editable columns on
 *   `f_billing_region_defaults`, FETCHED through `useBillingRegionLabels()` once
 *   by the table host. There is no `BILLING_REGION_LABELS` map and there must
 *   not be one (see `lib/billing-region.ts`).
 *
 * The context is the trailing parameter, so a column that needs neither may be
 * written `get: (row) => …`; the host always passes it.
 *
 * ⚠️ Money stays a decimal string here. `get` returns the string for the
 * page-scoped value filter; nothing in this file parses or sums it.
 */

/** What the table host passes to every accessor. */
export interface DrilldownCellContext {
  /** `meta.measuredInCurrency` — the currency of every `convertedAmount`. */
  measuredInCurrency: string;
  /** From `useBillingRegionLabels()`, called once by the host. */
  billingRegionLabel: BillingRegionLabeller;
}

/**
 * How a column's header filter behaves.
 * - `text`: a value list over the cell's text (open set: numbers, titles).
 * - `options`: a value list over a small closed-ish set (status, currency).
 * - `range`: a figure column. **`useTableFilters` has no page-scoped numeric
 *   filter** (its `range` is server params only, and this endpoint takes none),
 *   so {@link toColumnSpec} maps it to a right-aligned value list.
 */
export type DrilldownFilterKind = 'text' | 'options' | 'range';

export interface DrilldownColumn {
  /** Stable id: the ColumnManager stores hidden ids, so never rename one. */
  key: string;
  label: string;
  /** The filterable value. null / '' count as empty. */
  get: (row: TargetActualRow, ctx: DrilldownCellContext) => string | number | null;
  /** The cell. Omitted = the `get` text, `—` when empty. */
  render?: (row: TargetActualRow, ctx: DrilldownCellContext) => ReactNode;
  /** Right-align and tabular figures. */
  numeric?: boolean;
  /** The Excel cell. Omitted = `get`. Money columns return the decimal string. */
  exportValue?: (row: TargetActualRow, ctx: DrilldownCellContext) => string | number | null;
  /** Excel number format for this column (money columns: `#,##0.00`). */
  numFmt?: string;
  filter: DrilldownFilterKind;
  /** Desktop cell minimum width (CSS length); the column grows past it, never shrinks below. */
  minWidth?: string;
}

/** Excel format for every money column. */
export const MONEY_NUM_FMT = '#,##0.00';

const RECORD_PATH: Record<TargetActualRowKind, string> = {
  invoice: '/invoices',
  credit_note: '/credit-notes',
  quote: '/quotes',
  deal: '/deals',
};

/** The detail page of the document behind a row. */
export function recordPath(row: Pick<TargetActualRow, 'kind' | 'id'>): string {
  return `${RECORD_PATH[row.kind]}/${row.id}`;
}

const KIND_LABEL: Record<TargetActualRowKind, string> = {
  invoice: 'Invoice',
  credit_note: 'Credit note',
  quote: 'Quote',
  deal: 'Deal',
};

export function kindLabel(kind: TargetActualRowKind): string {
  return KIND_LABEL[kind];
}

/** A real router `<Link>` to the row's record. */
export function RecordLink({ row, children }: { row: TargetActualRow; children: ReactNode }) {
  return <TextLink to={recordPath(row)}>{children}</TextLink>;
}

/** The row's company, linked; `—` when none. */
export function CompanyCell({ row }: { row: TargetActualRow }) {
  if (!row.company) return <span className="text-fg-muted">—</span>;
  return <TextLink to={`/companies/${row.company.id}`}>{row.company.name}</TextLink>;
}

/** A `YYYY-MM-DD` date; `—` when null. */
export function DateCell({ value }: { value: string | null | undefined }) {
  return <span className="tabular-nums whitespace-nowrap">{formatDateOnly(value ?? null)}</span>;
}

/** A plain text cell; `—` when empty. */
export function TextCell({ value }: { value: string | number | null | undefined }) {
  if (value === null || value === undefined || String(value).trim() === '') {
    return <span className="text-fg-muted">—</span>;
  }
  return <>{value}</>;
}

/** The row's project names joined with `sep`; `null` when none or an older API. */
function projectNames(row: TargetActualRow, sep: string): string | null {
  if (!row.projects || row.projects.length === 0) return null;
  return row.projects.map((p) => p.name).join(sep);
}

/** The row's projects, each linked, comma-separated; `—` when none. */
export function ProjectsCell({ row }: { row: TargetActualRow }) {
  if (!row.projects || row.projects.length === 0) return <TextCell value={null} />;
  return (
    <span className="flex flex-wrap gap-1">
      {row.projects.map((p) => (
        <Link key={p.id} to={`/projects/${p.id}`} className="rounded-full no-underline">
          <Badge tone="neutral" variant="outline">
            {p.name}
          </Badge>
        </Link>
      ))}
    </span>
  );
}

/** The region's fetched name, or `null` for a company with no band. */
function regionName(row: TargetActualRow, ctx: DrilldownCellContext): string | null {
  return row.billingRegion === null ? null : ctx.billingRegionLabel(row.billingRegion);
}

/**
 * The columns every metric shares. Compose a metric's list from these, e.g.
 * `[COMMON_COLUMNS.number, COMMON_COLUMNS.title, …]`; {@link COMMON_COLUMN_LIST}
 * is the default order.
 */
export const COMMON_COLUMNS = {
  /**
   * The document number. Deals have none, so a deal's link sits on `title`
   * instead — each row carries exactly one record link.
   */
  number: {
    key: 'number',
    label: 'Number',
    get: (row) => row.number,
    render: (row) =>
      row.number ? <RecordLink row={row}>{row.number}</RecordLink> : <TextCell value={null} />,
    filter: 'text',
  },
  title: {
    key: 'title',
    label: 'Title',
    get: (row) => row.title,
    render: (row) =>
      row.number ? (
        <TextCell value={row.title} />
      ) : (
        <RecordLink row={row}>{row.title?.trim() ? row.title : `#${row.id}`}</RecordLink>
      ),
    filter: 'text',
    minWidth: '14rem',
  },
  company: {
    key: 'company',
    label: 'Company',
    get: (row) => row.company?.name ?? null,
    render: (row) => <CompanyCell row={row} />,
    filter: 'text',
    minWidth: '14rem',
  },
  /** The distinct projects on the document's lines (`row.projects`). */
  projects: {
    key: 'projects',
    label: 'Projects',
    get: (row) => projectNames(row, ', '),
    render: (row) => <ProjectsCell row={row} />,
    exportValue: (row) => projectNames(row, '; '),
    filter: 'text',
    minWidth: '21rem',
  },
  accountLead: {
    key: 'accountLead',
    label: 'Credited to',
    get: (row) => row.accountLead?.name ?? null,
    filter: 'options',
  },
  date: {
    key: 'date',
    label: 'Date',
    get: (row) => row.date,
    render: (row) => <DateCell value={row.date} />,
    exportValue: (row) => row.date,
    filter: 'text',
  },
  status: {
    key: 'status',
    label: 'Status',
    get: (row) => row.status,
    filter: 'options',
  },
  billingRegion: {
    key: 'billingRegion',
    label: 'Billing region',
    get: regionName,
    filter: 'options',
  },
  currency: {
    key: 'currency',
    label: 'Currency',
    get: (row) => row.currency,
    filter: 'options',
  },
  amount: {
    key: 'amount',
    label: 'Amount',
    get: (row) => row.amount,
    render: (row) => <Money amount={row.amount} currency={row.currency} />,
    numeric: true,
    numFmt: MONEY_NUM_FMT,
    filter: 'range',
  },
  convertedAmount: {
    key: 'convertedAmount',
    label: 'Converted',
    get: (row) => row.convertedAmount,
    render: (row, ctx) => <Money amount={row.convertedAmount} currency={ctx.measuredInCurrency} />,
    numeric: true,
    numFmt: MONEY_NUM_FMT,
    filter: 'range',
  },
} satisfies Record<string, DrilldownColumn>;

export type CommonColumnKey = keyof typeof COMMON_COLUMNS;

/** The shared columns in their default order. */
export const COMMON_COLUMN_LIST: DrilldownColumn[] = [
  COMMON_COLUMNS.number,
  COMMON_COLUMNS.title,
  COMMON_COLUMNS.company,
  COMMON_COLUMNS.projects,
  COMMON_COLUMNS.accountLead,
  COMMON_COLUMNS.date,
  COMMON_COLUMNS.status,
  COMMON_COLUMNS.billingRegion,
  COMMON_COLUMNS.currency,
  COMMON_COLUMNS.amount,
  COMMON_COLUMNS.convertedAmount,
];

/** The cell a host renders for a column. */
export function renderDrilldownCell(
  column: DrilldownColumn,
  row: TargetActualRow,
  ctx: DrilldownCellContext,
): ReactNode {
  if (column.render) return column.render(row, ctx);
  return <TextCell value={column.get(row, ctx)} />;
}

/** The Excel value a host writes for a column. */
export function exportDrilldownValue(
  column: DrilldownColumn,
  row: TargetActualRow,
  ctx: DrilldownCellContext,
): string | number | null {
  return column.exportValue ? column.exportValue(row, ctx) : column.get(row, ctx);
}

/**
 * A drill-down column as a `useTableFilters` column. Every filter is
 * page-scoped (the whole response is loaded, no paging) and there is no
 * `sortKey` (the hook never sorts locally). `range` becomes a right-aligned
 * value list — see {@link DrilldownFilterKind}.
 */
export function toColumnSpec(
  column: DrilldownColumn,
  ctx: DrilldownCellContext,
): ColumnSpec<TargetActualRow> {
  return {
    key: column.key,
    label: column.label,
    get: (row) => column.get(row, ctx),
    scope: 'page',
    numeric: column.numeric || column.filter === 'range' ? true : undefined,
  };
}
